import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ChatWindow, { chatApi } from "@/components/ChatWindow";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, FileText, Plus } from "lucide-react";

const STATUS_LABEL = { nouveau: "Nouveau", en_cours: "En cours", traite: "Traité" };
const STATUS_COLOR = {
  nouveau: "bg-[#C86641]/10 text-[#C86641]",
  en_cours: "bg-amber-100 text-amber-700",
  traite: "bg-emerald-100 text-emerald-700",
};

export default function ClientDashboard() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [tab, setTab] = useState("chat");

  const loadQuotes = useCallback(async () => {
    try {
      const { data } = await api.get("/quotes/mine");
      setQuotes(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadQuotes(); }, [loadQuotes]);

  return (
    <div className="bg-[#FAFAFA] text-[#1A1918] min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-28 md:pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#C86641]">Espace client</div>
              <h1 className="font-display text-4xl md:text-5xl tracking-tighter mt-3" data-testid="client-greeting">
                Bonjour {user?.name?.split(" ")[0] || "client"}.
              </h1>
            </div>
            <Link
              to="/devis"
              data-testid="client-new-devis"
              className="bg-[#C86641] text-[#FAFAFA] px-6 py-3 font-semibold hover:bg-[#A65132] transition-colors inline-flex items-center gap-2"
            >
              <Plus size={16} /> Nouvelle demande de devis
            </Link>
          </div>

          <div className="mt-10 flex gap-2 border-b border-[#E5E3DD]">
            <TabBtn active={tab === "chat"} onClick={() => setTab("chat")} icon={<MessageCircle size={16} />} label="Discussion" testId="client-tab-chat" />
            <TabBtn active={tab === "devis"} onClick={() => setTab("devis")} icon={<FileText size={16} />} label={`Mes devis (${quotes.length})`} testId="client-tab-devis" />
          </div>

          {tab === "chat" ? (
            <div className="mt-6 h-[70vh] min-h-[500px]">
              <ChatWindow
                fetchMessages={chatApi.myMessages}
                sendMessage={chatApi.sendMine}
                currentRole="client"
                emptyText="Posez vos questions ici. Je vous réponds rapidement."
                placeholder="Bonjour, j'ai une question concernant…"
              />
            </div>
          ) : (
            <div className="mt-6">
              {quotes.length === 0 ? (
                <div className="border border-[#E5E3DD] bg-[#F3F1ED] p-10 text-center">
                  <p className="text-[#5C5A56]">Vous n'avez pas encore de demande de devis.</p>
                  <Link to="/devis" className="mt-6 inline-block bg-[#1A1918] text-[#FAFAFA] px-6 py-3 font-semibold hover:bg-[#C86641] transition-colors" data-testid="client-create-first-devis">
                    Faire une demande
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {quotes.map((q) => (
                    <div key={q.id} data-testid={`client-quote-${q.id}`} className="border border-[#E5E3DD] bg-[#FAFAFA] p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <div className="font-semibold text-lg">{q.project_type}</div>
                        <div className="text-sm text-[#5C5A56] mt-1 line-clamp-2 max-w-2xl">{q.description}</div>
                        <div className="text-xs text-[#5C5A56] mt-2">Envoyé le {new Date(q.created_at).toLocaleDateString("fr-FR")}</div>
                      </div>
                      <span className={`text-xs px-3 py-1.5 font-bold tracking-wider uppercase ${STATUS_COLOR[q.status]}`}>
                        {STATUS_LABEL[q.status]}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-3 text-sm font-semibold inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${
        active ? "border-[#C86641] text-[#1A1918]" : "border-transparent text-[#5C5A56] hover:text-[#1A1918]"
      }`}
    >
      {icon} {label}
    </button>
  );
}
